/**
 * FctEstadoTag — Tag corporativo para representar el estado de una operación.
 *
 * Patrón canónico de la app GCvsc: en cualquier listado o tabla donde haya
 * un campo "estado" (numérico o textual), este componente lo renderiza con
 * el color y etiqueta consistentes en toda la app.
 *
 * Uso por id numérico:
 *   <FctEstadoTag estado={0} />            // → "Pendiente" (naranja)
 *   <FctEstadoTag estado={1} />            // → "Realizada" (verde)
 *   <FctEstadoTag estado={2} />            // → "Descartada" (gris)
 *
 * Uso por clave textual:
 *   <FctEstadoTag estado="pendiente" />
 *   <FctEstadoTag estado="realizada" />
 *
 * Uso con sufijo (concordancia gramatical):
 *   <FctEstadoTag estado={1} genero="masculino" />  // → "Realizado"
 *
 * Variante traslado:
 *   <FctEstadoTag estado={3} variante="traslado" />  // → "Formato"
 *
 * Si el `estado` no se reconoce, se renderiza un Tag por defecto con el
 * texto `Estado {valor}` para no romper la UI.
 */

import React from 'react';
import { Tag } from 'antd';

export type FctEstadoVariante = 'operacion' | 'traslado';
export type FctEstadoGenero = 'femenino' | 'masculino';

interface EstadoDef {
  /** Etiqueta en femenino (por defecto, la más usada en GCvsc). */
  labelF: string;
  /** Etiqueta en masculino. */
  labelM: string;
  /** Color Ant Design del Tag. */
  color: string;
}

// Mapa de estados — usa nombres de color de AntD para que el Tag elija
// automáticamente el fondo y borde apropiados de la paleta semántica.
const ESTADOS_OPERACION: Record<number, EstadoDef> = {
  0: { labelF: 'Pendiente', labelM: 'Pendiente', color: 'orange' },
  1: { labelF: 'Realizada', labelM: 'Realizado', color: 'green' },
  2: { labelF: 'Descartada', labelM: 'Descartado', color: 'default' },
};

const ESTADOS_TRASLADO: Record<number, EstadoDef> = {
  0: { labelF: 'Pendiente', labelM: 'Pendiente', color: 'orange' },
  1: { labelF: 'Traslado OK', labelM: 'Traslado OK', color: 'green' },
  2: { labelF: 'Descartada', labelM: 'Descartado', color: 'default' },
  3: { labelF: 'Formato', labelM: 'Formato', color: 'blue' },
  4: { labelF: 'Cambio nombre', labelM: 'Cambio nombre', color: 'purple' },
};

// Alias textuales (admiten variantes en singular)
const TEXT_ALIASES: Record<string, number> = {
  pendiente: 0,
  realizada: 1, realizado: 1, ok: 1,
  descartada: 2, descartado: 2,
  formato: 3,
  'cambio nombre': 4, 'cambio-nombre': 4,
};

interface FctEstadoTagProps {
  /** Id numérico (0/1/2/...) o clave textual ("pendiente", "realizada", ...). */
  estado: number | string;
  /** Variante de paleta de estados. Por defecto, "operacion". */
  variante?: FctEstadoVariante;
  /** Género gramatical de la etiqueta (femenino por defecto). */
  genero?: FctEstadoGenero;
  /** Etiqueta personalizada (anula el lookup). */
  label?: string;
  /** Para forzar un color que sobreescriba el del lookup. */
  color?: string;
}

export const FctEstadoTag: React.FC<FctEstadoTagProps> = ({
  estado,
  variante = 'operacion',
  genero = 'femenino',
  label,
  color,
}) => {
  const tabla = variante === 'traslado' ? ESTADOS_TRASLADO : ESTADOS_OPERACION;

  // Resolver clave textual a id numérico
  let id: number;
  if (typeof estado === 'number') {
    id = estado;
  } else {
    const key = estado.toString().trim().toLowerCase();
    id = TEXT_ALIASES[key] ?? -1;
  }

  const def = tabla[id];
  if (!def) {
    return (
      <Tag color={color ?? 'default'}>
        {label ?? `Estado ${estado}`}
      </Tag>
    );
  }

  return (
    <Tag color={color ?? def.color}>
      {label ?? (genero === 'masculino' ? def.labelM : def.labelF)}
    </Tag>
  );
};
